import React, { useState, useEffect, useContext, memo, useMemo } from 'react';
import { useFormik } from 'formik';
import Input from '../../bootstrap/forms/Input';
import Checks, { ChecksGroup } from '../../bootstrap/forms/Checks';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '../../bootstrap/Modal';
import PreAlert from '../../../helpers/utils/preAlert';
import { DefaultContext } from '../../../contexts/default';
import BtnLoad from '../../MyCustom/BtnLoad';
import BtnCancel from '../../MyCustom/BtnCancel';
import BtnConfirm from '../../MyCustom/BtnConfirm';
import CouponDB from '../../../database/wrappers/coupon';
import CategoryDB from '../../../database/wrappers/category';
import FormGroup from '../../bootstrap/forms/FormGroup';
import masks, { unmask } from '../../../helpers/utils/masks';

const validate = (values) => {
  const errors = {};

  const isFixedDiscount = values.discountType === 'fixed';
  const discountValue = isFixedDiscount
    ? Number(unmask(String(values.discountValue)))
    : Number(values.discountValue);
  if (!values.discountValue) {
    errors.discountValue = 'Este campo é necessário.';
  } else if (discountValue <= 0) {
    errors.discountValue = 'Informe um desconto maior que zero.';
  } else if (!isFixedDiscount && discountValue > 100) {
    errors.discountValue = 'Informe uma porcentagem entre 1 e 100.';
  }

  if (!values.cod) {
    errors.cod = 'Este campo é necessário.';
  }
  if (values.restrictCategories && values.categoryIds.length === 0) {
    errors.categoryIds = 'Selecione pelo menos uma categoria.';
  }

  return errors;
}

const ModalRegisterCoupon = ({ open, setIsOpen, setIsClose, couponSelected }) => {

  const { onShowAlert, establishment, store } = useContext(DefaultContext)

  const [isLoading, setIsLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categorySearch, setCategorySearch] = useState('');

  const filteredCategories = useMemo(() => {
    const search = categorySearch.trim().toLowerCase();
    if (!search) return categories;
    return categories.filter(category =>
      category.name?.toLowerCase().includes(search)
    );
  }, [categories, categorySearch]);

  useEffect(() => {
    if (!open || !establishment?.id) return;

    new CategoryDB(establishment.id)
      .getAll()
      .then(data => setCategories(
        data
          .filter(category => category.active)
          .sort((a, b) => a.name.localeCompare(b.name))
      ))
      .catch(() => {
        setCategories([]);
        onShowAlert(PreAlert.error('Não foi possível carregar as categorias'));
      });
  }, [open, establishment?.id, onShowAlert]);

  useEffect(() => {
    if (!open) {
      setCategorySearch('');
      return formik.resetForm();
    }
    if (couponSelected) {
      const {
        cod,
        percentage,
        discountType,
        discountValue,
        active,
        categoryIds,
      } = couponSelected;
      const currentDiscountValue = discountValue ?? percentage ?? '';

      formik.setValues({
        cod,
        discountValue: currentDiscountValue,
        discountType: discountType || 'percentage',
        active: active,
        categoryIds: categoryIds ?? [],
        restrictCategories: Boolean(categoryIds?.length),
      });
    }
  }, [couponSelected, open])

  const formik = useFormik({
    initialValues: {
      cod: '',
      discountValue: '',
      discountType: 'percentage',
      active: true,
      categoryIds: [],
      restrictCategories: false,
    },
    validate,
    onSubmit: values => {
      const {
        cod,
        discountValue,
        discountType,
        active,
        categoryIds,
        restrictCategories,
      } = values;
      const isFixedDiscount = discountType === 'fixed';
      const parsedDiscountValue = isFixedDiscount
        ? Number(unmask(String(discountValue)))
        : Number(discountValue);
      const data = {
        discountType,
        discountValue: parsedDiscountValue,
        percentage: isFixedDiscount ? null : parsedDiscountValue,
        cod: cod.trim().toUpperCase(),
        active,
        categoryIds: restrictCategories ? categoryIds : [],
      }

      const onSuccess = () => {
        onShowAlert(PreAlert.success('Registro cadastrado com sucesso'))
        setIsLoading(false);
        setIsClose();
      }
      const onSuccessUpdate = () => {
        onShowAlert(PreAlert.success('Registro atualizado com sucesso'))
        setIsLoading(false);
        setIsClose();
      }
      const onError = () => {
        onShowAlert(PreAlert.error('Falhou ao cadastrar o registro'))
        setIsLoading(false);
      }

      setIsLoading(true);
      if (couponSelected) {
        new CouponDB(establishment.id, store.id)
          .update(couponSelected.id, data)
          .then(onSuccessUpdate)
          .catch(onError)
          .finally(() => setIsLoading(false))
      } else {
        new CouponDB(establishment.id, store.id)
          .create(data)
          .then(onSuccess)
          .catch(onError)
          .finally(() => setIsLoading(false))
      }
    },
  })

  return (
    <Modal
      id={'modal-register-ticketBoxEntrance'}
      titleId={'Cadastro e Atualização de Entradas'}
      isOpen={open}
      setIsOpen={setIsOpen}
      isStaticBackdrop={true}
      isScrollable={false}
      isCentered={true}
      size="lg" // 'sm' || 'lg' || 'xl'
      isAnimation={true}
      onSubmit={formik.handleSubmit}
    >
      <ModalHeader setIsOpen={setIsOpen}>
        <ModalTitle id="register-couponSelected">{couponSelected ? 'Atualização de Cupom' : 'Cadastro de Cupom'}</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <form noValidate onSubmit={formik.handleSubmit}>

          {/* Inputs */}
          <div className="row g-4 px-2">

            {/* Codigo */}
            <FormGroup id="cod" label="Código" className='col-md-4'>
              <Input
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.cod}
                isValid={formik.isValid}
                isTouched={formik.touched.cod}
                invalidFeedback={formik.errors.cod}
                validFeedback='Assim está bom!'
                placeholder='Ex: LANCHES10'
              />
            </FormGroup>

            <FormGroup label='Tipo de desconto' className='col-md-5'>
              <ChecksGroup>
                <Checks
                  type='radio'
                  id='discountTypePercentage'
                  name='discountType'
                  label='Porcentagem (%)'
                  value='percentage'
                  checked={formik.values.discountType}
                  onChange={event => {
                    formik.handleChange(event);
                    formik.setFieldValue('discountValue', '');
                  }}
                  isInline
                />
                <Checks
                  type='radio'
                  id='discountTypeFixed'
                  name='discountType'
                  label='Valor fixo (R$)'
                  value='fixed'
                  checked={formik.values.discountType}
                  onChange={event => {
                    formik.handleChange(event);
                    formik.setFieldValue('discountValue', '');
                  }}
                  isInline
                />
              </ChecksGroup>
            </FormGroup>

            <FormGroup
              id='discountValue'
              label={formik.values.discountType === 'fixed' ? 'Desconto (R$)' : 'Desconto (%)'}
              className='col-md-3'>
              <Input
                type={formik.values.discountType === 'fixed' ? 'text' : 'number'}
                ariaDescribedby='inputGroupPrepend'
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.discountType === 'fixed'
                  ? masks.money(String(formik.values.discountValue || ''))
                  : formik.values.discountValue}
                isValid={formik.isValid}
                isTouched={formik.touched.discountValue}
                invalidFeedback={formik.errors.discountValue}
                validFeedback='Assim está bom!'
                placeholder={formik.values.discountType === 'fixed' ? 'Ex: 10,00' : 'Ex: 10'}
              />
            </FormGroup>

            <div className='col-md-12'>
              <div className='border rounded p-3'>
                <div className='d-flex align-items-center justify-content-between'>
                  <div>
                    <h6 className='mb-1'>Aplicação do cupom</h6>
                    <small className='text-muted'>
                      {formik.values.restrictCategories
                        ? 'O desconto será aplicado somente nas categorias selecionadas.'
                        : 'O desconto será aplicado a todas as categorias.'}
                    </small>
                  </div>
                  <Checks
                    type='switch'
                    id='restrictCategories'
                    name='restrictCategories'
                    label='Selecionar categorias'
                    checked={formik.values.restrictCategories}
                    onChange={event => {
                      formik.handleChange(event);
                      if (!event.target.checked) {
                        formik.setFieldValue('categoryIds', []);
                      }
                    }}
                  />
                </div>

                {formik.values.restrictCategories && (
                  <>
                    <FormGroup id='categorySearch' className='mt-3'>
                      <Input
                        value={categorySearch}
                        onChange={event => setCategorySearch(event.target.value)}
                        placeholder='Buscar categoria'
                      />
                    </FormGroup>

                    <div
                      className='overflow-auto mt-3'
                      style={{ maxHeight: 280 }}
                    >
                      {filteredCategories.map(category => {
                        const checked = formik.values.categoryIds.includes(category.id);
                        return (
                          <div
                            key={category.id}
                            className='d-flex align-items-center justify-content-between border-bottom py-2 px-1'
                          >
                            <div className='d-flex align-items-center gap-2'>
                              {category.image_url && (
                                <img
                                  src={category.image_url}
                                  alt=''
                                  width={36}
                                  height={36}
                                  className='rounded'
                                />
                              )}
                              <span>{category.name}</span>
                            </div>
                            <Checks
                              type='switch'
                              id={`category-${category.id}`}
                              name='categoryIds'
                              checked={checked}
                              onChange={() => {
                                const nextCategoryIds = checked
                                  ? formik.values.categoryIds.filter(id => id !== category.id)
                                  : [...formik.values.categoryIds, category.id];
                                formik.setFieldValue('categoryIds', nextCategoryIds);
                                formik.setFieldTouched('categoryIds', true);
                              }}
                            />
                          </div>
                        );
                      })}

                      {filteredCategories.length === 0 && (
                        <p className='text-muted text-center py-3 mb-0'>
                          Nenhuma categoria encontrada.
                        </p>
                      )}
                    </div>

                    {formik.touched.categoryIds && formik.errors.categoryIds && (
                      <div className='text-danger mt-2'>
                        {formik.errors.categoryIds}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Status */}
            <FormGroup
              id='active'
              label='Status'
              className='col-md-7'
            >
              <Checks
                type="switch"
                id="active"
                label={formik.values.active ? 'Ativo' : 'Inativo'}
                name="active"
                onChange={formik.handleChange}
                checked={formik.values.active}
              />
            </FormGroup>

          </div>
          {/* Buttons */}
          <div className="row pt-4">
            <div className="col-md-5 offset-md-7">
              <div className='d-flex justify-content-evenly'>
                <BtnCancel setIsClose={setIsClose} />
                {isLoading ? (
                  <BtnLoad />
                ) : (
                  <BtnConfirm isDisable={!formik.isValid} />
                )}
              </div>
            </div>
          </div>
        </form>
      </ModalBody>
    </Modal>
  )
}

export default memo(ModalRegisterCoupon);
